import { Suspense, useEffect, useRef } from 'react';

import { Box, styled } from '@mui/material';
import { useGetPaymentProcessor } from '@pflegenavi/frontend/api-nursing-home';
import { useAuthentication } from '@pflegenavi/frontend/authentication';
import { useFeatureFlagModal } from '@pflegenavi/frontend/feature-flags-web';
import { DashboardLayout } from '@pflegenavi/frontend/layout';
import {
  DevLangSwitcher,
  localeStorage,
} from '@pflegenavi/frontend/localization-web-app';
import { spAppMainPages } from '@pflegenavi/frontend/routing';
import {
  useGetNursingHomeList,
  useGetServiceProviderPhoenix,
} from '@pflegenavi/service-provider-frontend/api';
import { ConnectionStatus } from '@pflegenavi/service-provider-shared/api-endpoints';
import {
  Login,
  RedirectResetPassword,
} from '@pflegenavi/shared-web/authentication';
import { useSyncLocaleWithServer } from '@pflegenavi/shared/frontend';
import { PageNotFound } from '@pflegenavi/web-components';
import { Navigate, Route, useLocation, useNavigate } from 'react-router-dom';
import { renderSwitchLanguageMenuItem } from '../components/general/SwitchLanguageMenuItem';
import { ServiceProviderProfileDetails } from '../components/profile/ServiceProviderProfileDetails';
import { ServiceProviderProfileSettings } from '../components/profile/ServiceProviderProfileSettings';
import ServiceProviderStripeOnboardingComplete from '../components/profile/ServiceProviderStripeOnboardingComplete';
import ServiceProviderStripeOnboardingRefresh from '../components/profile/ServiceProviderStripeOnboardingRefresh';
import { AddReceiptBatchPage } from '../pages/AddReceiptBatchPage';
import { DashboardPage } from '../pages/DashboardPage';
import EditReceiptBatchPage from '../pages/EditReceiptBatchPage';
import { PayoutsPage } from '../pages/PayoutsPage';
import { ProfilePage } from '../pages/ProfilePage';
import { ServicesOverviewPage } from '../pages/ServicesOverviewPage';
import { SetupBankAccountPage } from '../pages/SetupBankAccountPage';
import { SentryRoutes } from '../sentry';
import { ConnectApp } from './ConnectApp';
import { ScaEnrollmentButton } from '../components/sca/ScaEnrollmentButton';

export default function App(): JSX.Element {
  const { user } = useAuthentication();
  const location = useLocation();

  useSyncLocaleWithServer({ localeStorage });

  useEffect(() => {
    // @ts-expect-error Beacon is untyped
    if (!window.Beacon || !user) {
      return;
    }

    // @ts-expect-error Beacon is untyped
    window.Beacon('identify', {
      name: `${user.firstName} ${user.lastName}`,
      email: user?.email,
      tenant: 'service_provider',
      'user-id': user.userId,
      environment: window.location.hostname.startsWith('localhost')
        ? 'local'
        : window.location.hostname.includes('.staging.')
        ? 'staging'
        : 'production',
      application: 'Service Provider web',
    });
  }, [user]);

  useEffect(() => {
    // @ts-expect-error Beacon is untyped
    if (!window.Beacon) {
      return;
    }

    // @ts-expect-error Beacon is untyped
    window.Beacon('event', {
      type: 'page-viewed',
      url: location.pathname,
      title: document.title,
    });
    // @ts-expect-error Beacon is untyped
    window.Beacon('suggest');
    if (location && location.pathname === '/') {
      // @ts-expect-error Beacon is untyped
      window.Beacon('navigate', '/');
    }
  }, [location.pathname, location]);

  if (!user) {
    return (
      <>
        <Login />
      </>
    );
  }

  return <AppInner />;
}

const AppInner = () => {
  const navigate = useNavigate();

  const { FeatureFlagModalComponent } = useFeatureFlagModal();

  const { redirect } = useRedirectToConnectAccountPage();

  const paymentProcessor = useGetPaymentProcessor();
  const { data: serviceProvider } = useGetServiceProviderPhoenix();

  if (redirect) {
    return <ConnectApp />;
  }

  return (
    <Suspense fallback={<LoadingContainer />}>
      {FeatureFlagModalComponent}
      {!import.meta.env.PROD && <DevLangSwitcher />}
      <SentryRoutes>
        <Route
          path={'/reset-password/:token'}
          element={<RedirectResetPassword />}
        />
        <Route
          path={spAppMainPages.CONNECT_ACCOUNT}
          element={<Navigate to={spAppMainPages.DASHBOARD} replace />}
        />
        <Route
          path={spAppMainPages.NEW_INVITATION}
          element={<Navigate to={spAppMainPages.DASHBOARD} replace />}
        />
        <Route
          path={spAppMainPages.SETUP_BANK_ACCOUNT}
          element={<SetupBankAccountPage />}
        />
        <Route
          path={spAppMainPages.HOME}
          element={
            <DashboardLayout
              configKey="sp-web"
              helpUrl={
                'https://pn.helpscoutdocs.com/category/140-service-provider-webapp'
              }
              home={spAppMainPages.HOME}
              renderSwitchLanguageMenuItem={renderSwitchLanguageMenuItem}
              menuItems={
                paymentProcessor === 'mangopay' &&
                serviceProvider?.data?.mangopay_sca_enrolled === false
                  ? [<ScaEnrollmentButton key="sca-enrollment" />]
                  : undefined
              }
            />
          }
        >
          <Route path={spAppMainPages.DASHBOARD} element={<DashboardPage />} />
          <Route
            path={spAppMainPages.RECEIPT_BATCH_NEW}
            element={<AddReceiptBatchPage />}
          />
          <Route path={spAppMainPages.PAYOUTS} element={<PayoutsPage />} />
          <Route
            path={spAppMainPages.RECEIPT_BATCH_EDIT}
            element={<EditReceiptBatchPage />}
          />
          <Route
            path={spAppMainPages.SERVICES}
            element={<ServicesOverviewPage />}
          />
          <Route
            path={spAppMainPages.HOME}
            element={<Navigate to={spAppMainPages.DASHBOARD} replace />}
          />
          <Route
            path={spAppMainPages.WELCOME}
            element={<Navigate to={spAppMainPages.DASHBOARD} replace />}
          />
          <Route path={spAppMainPages.PROFILE} element={<ProfilePage />}>
            <Route index={true} element={<ServiceProviderProfileDetails />} />
          </Route>
          {paymentProcessor === 'mangopay' && (
            <Route path={spAppMainPages.SETTINGS} element={<ProfilePage />}>
              <Route
                index={true}
                element={<ServiceProviderProfileSettings />}
              />
            </Route>
          )}
          <Route
            path={spAppMainPages.STRIPE_ONBOARDING_COMPLETE}
            element={<ServiceProviderStripeOnboardingComplete />}
          />
          <Route
            path={spAppMainPages.STRIPE_ONBOARDING_REFRESH}
            element={<ServiceProviderStripeOnboardingRefresh />}
          />
          <Route
            path="*"
            element={
              <PageNotFound
                handleGoHomeClick={() => navigate(spAppMainPages.HOME)}
              />
            }
          />
        </Route>
      </SentryRoutes>
    </Suspense>
  );
};

const useRedirectToConnectAccountPage = () => {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const { user } = useAuthentication();
  const globalServiceProviderId = user?.userId;

  const result = useGetNursingHomeList({});
  const { data: nursingHomeList } = result;

  // Get service provider to check MangoPay setup status
  const { data: serviceProvider } = useGetServiceProviderPhoenix();

  const hasActiveNursingHome = Boolean(
    nursingHomeList?.find(
      (nursingHome) => nursingHome.connectionStatus === ConnectionStatus.Active
    )
  );
  const pendingNursingHome = nursingHomeList?.find(
    (nursingHome) =>
      nursingHome.connectionStatus === ConnectionStatus.InvitePending
  );

  const hasPendingNursingHome = Boolean(pendingNursingHome);

  // Check if MangoPay setup is incomplete (matching mobile's onboarding-check logic)
  const mangopaySetupIncomplete =
    serviceProvider?.data?.payment_service_provider === 'mangopay' &&
    !serviceProvider?.data?.mangopay_user_id;

  useEffect(() => {
    // Priority 1: Pending invitation
    if (hasPendingNursingHome) {
      if (!hasActiveNursingHome) {
        navigateRef.current(
          {
            pathname: `${spAppMainPages.CONNECT_ACCOUNT}?tenant=${
              pendingNursingHome!.tenant
            }&nursingHomeId=${
              pendingNursingHome!.nursingHomeId
            }&globalServiceProviderId=${
              pendingNursingHome!.globalServiceProviderId
            }`,
          },
          { replace: true }
        );
      } else {
        navigateRef.current(
          {
            pathname: `${spAppMainPages.NEW_INVITATION}?tenant=${
              pendingNursingHome!.tenant
            }&nursingHomeId=${
              pendingNursingHome!.nursingHomeId
            }&globalServiceProviderId=${
              pendingNursingHome!.globalServiceProviderId
            }`,
          },
          { replace: true }
        );
      }
      return; // Exit early if handling invitation
    }

    // Priority 2: MangoPay user account setup incomplete
    if (mangopaySetupIncomplete && globalServiceProviderId) {
      const targetUrl = `${spAppMainPages.CONNECT_ACCOUNT}?globalServiceProviderId=${globalServiceProviderId}`;

      navigateRef.current(
        {
          pathname: targetUrl,
        },
        { replace: true }
      );
      return; // Exit early after redirecting
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasActiveNursingHome,
    navigateRef,
    hasPendingNursingHome,
    pendingNursingHome?.tenant,
    pendingNursingHome?.nursingHomeName,
    pendingNursingHome?.nursingHomeId,
    pendingNursingHome?.globalServiceProviderId,
    mangopaySetupIncomplete,
    globalServiceProviderId,
  ]);

  return {
    redirect: hasPendingNursingHome || mangopaySetupIncomplete,
  };
};

const LoadingContainer = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    display: 'flex',
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  })
);
